import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Zap, Sparkles } from 'lucide-react';

/**
 * ModelSelector - Compact dropdown for selecting AI model
 *
 * Editorial/monospace aesthetic with smooth micro-interactions
 */

const MODELS = [
  {
    id: 'gemini-2.5-flash',
    label: 'Flash',
    description: 'Fast',
    icon: Zap,
    color: '#f59e0b' // warm amber
  },
  {
    id: 'gemini-3-flash-preview',
    label: 'Pro',
    description: 'Quality',
    icon: Sparkles,
    color: '#8b5cf6' // purple
  }
];

const ModelSelector = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedModel = MODELS.find(m => m.id === value) || MODELS[0];
  const IconComponent = selectedModel.icon;

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (modelId) => {
    onChange(modelId);
    setIsOpen(false);
  };

  return (
    <div className="sp-model-selector" ref={dropdownRef}>
      <button
        type="button"
        className={`sp-model-selector__trigger ${isOpen ? 'sp-model-selector__trigger--open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className="sp-model-selector__icon"
          style={{ '--model-color': selectedModel.color }}
        >
          <IconComponent size={12} />
        </span>
        <span className="sp-model-selector__label">{selectedModel.label}</span>
        <ChevronDown
          size={12}
          className={`sp-model-selector__chevron ${isOpen ? 'sp-model-selector__chevron--open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="sp-model-selector__dropdown" role="listbox">
          <div className="sp-model-selector__dropdown-header">
            Select Model
          </div>
          {MODELS.map((model) => {
            const ModelIcon = model.icon;
            const isSelected = model.id === value;

            return (
              <button
                key={model.id}
                type="button"
                className={`sp-model-selector__option ${isSelected ? 'sp-model-selector__option--selected' : ''}`}
                onClick={() => handleSelect(model.id)}
                role="option"
                aria-selected={isSelected}
              >
                <span
                  className="sp-model-selector__option-icon"
                  style={{ '--model-color': model.color }}
                >
                  <ModelIcon size={14} />
                </span>
                <span className="sp-model-selector__option-content">
                  <span className="sp-model-selector__option-label">{model.label}</span>
                  <span className="sp-model-selector__option-desc">{model.description}</span>
                </span>
                {isSelected && (
                  <span className="sp-model-selector__check">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ModelSelector;
